import {customElement, property, query, state} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {VideoProgress, VideoTracking, VideoTrackingFactory} from "../../tracking/videoTracking";
import {resolve} from "../../container";
import {GLOBAL} from "../../common/globals";
import {ManagingResources} from "../../common/lifetime";
import {KEYS, prepareEvents} from "../../common/utils/events";
import type {Nullable} from "../../bootstrap/common/types";
import {Resolution} from "../../common/resolution";
import {BOOTSTRAP} from "../../common/resolutionConstants";
import {ClickTracking} from "../../tracking/clickTracking";
import {EventBus} from "../../common/eventBus";
import {DIALOG_CLOSED_EVENT, DIALOG_OPENED_EVENT} from "./dialog/dialog";
import {unsafeSVG} from "lit/directives/unsafe-svg.js";
import {PAUSE_ICON, PLAY_ICON} from "../../common/svgIcons";
import {UnLitElement} from "../../common/elements";

/* on Apple devices, preview image is shown only when thumbnail is provided. Therefore, we skip to first frame to show this as preview image.
Number must be very small to ensure progress bar is still on the very left
 */
const STARTING_TIME = "0.0000000001";

@customElement("eop-video")
export class EopVideo extends UnLitElement {

    @query("video")
    private videoElement: HTMLVideoElement;

    @property({attribute: "subtitle-src"})
    private subtitleSrc: string;
    @property({attribute: "thumbnail-src"})
    private thumbnailSrc: string;
    @property({attribute: "video-alt"})
    private alt: string;
    @property({attribute: "video-src"})
    private src: string;
    @property({attribute: "video-src-mobile"})
    private srcMobile: string;
    @property({attribute: "rounded", reflect: true, type: Boolean})
    public rounded: boolean;
    @state()
    private currentSrc: string;

    private tracking: VideoTracking;
    private readonly handlePausing = (): void => {
        this.pause();
    };

    public constructor(
        private videoTrackingFactory: VideoTrackingFactory = resolve(VideoTrackingFactory),
        private resolution: Resolution = resolve(Resolution),
        private eventBus: EventBus = resolve(EventBus)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.resolution.onBootstrapBreakpointChange(() => this.updateVideoSource());
        this.updateVideoSource();
        this.eventBus.on(DIALOG_OPENED_EVENT, this.handlePausing);
        this.eventBus.on(DIALOG_CLOSED_EVENT, this.handlePausing);
    }

    public render(): TemplateResult {
        return html`
            <video controls playsinline preload="metadata" poster=${this.thumbnailSrc} class="video-player">
                <source src=${this.currentSrc}/>
                ${this.alt}
                ${this.renderSubtitle()}
            </video>
        `;
    }

    private updateVideoSource(): void {
        if (this.resolution.upTo(BOOTSTRAP.XS)) {
            this.currentSrc = this.videoSrc(this.srcMobile ?? this.src);
        } else {
            this.currentSrc = this.videoSrc(this.src);
        }
        this.videoElement?.load();
    }

    private videoSrc(src: string): string {
        if (this.thumbnailSrc) {
            return src;
        } else {
            return src + "#t=" + STARTING_TIME;
        }
    }

    private renderSubtitle(): Nullable<TemplateResult> {
        if (this.subtitleSrc) {
            return html`
                <track default kind="captions" src=${this.subtitleSrc}>`;
        }
        return null;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        this.tracking = this.videoTrackingFactory.create(this.alt ?? "", new HtmlVideoProgress(this.videoElement));
        this.videoElement.addEventListener("play", () => {
            this.tracking.playing();
        });
        this.videoElement.addEventListener("pause", () => {
            this.tracking.pause();
        });
        this.videoElement.addEventListener("ended", () => {
            this.tracking.ended();
        });
    }

    public pause(): void {
        this.videoElement?.pause();
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.eventBus.remove(DIALOG_OPENED_EVENT, this.handlePausing);
        this.eventBus.remove(DIALOG_CLOSED_EVENT, this.handlePausing);
        if (this.tracking) {
            this.tracking.disconnect();
        }
    }
}

export class HtmlVideoProgress extends VideoProgress {
    public constructor(private videoElement: HTMLVideoElement) {
        super();
    }

    public currentTime = (): number => this.videoElement.currentTime;
    public duration = (): number => this.videoElement.duration;
}

export abstract class PausableVideoLoop extends ManagingResources(UnLitElement) {

    @query("video")
    protected videoElement: HTMLVideoElement;

    @property({attribute: "video-src"})
    protected src: string;
    @property({attribute: "video-alt"})
    protected alt: string;

    @state()
    protected videoInitialPlaying: boolean = false;
    @state()
    protected videoPlaying: boolean = false;

    protected constructor(
        private clickTracking: ClickTracking = resolve(ClickTracking),
        private eventBus: EventBus = resolve(EventBus)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        prepareEvents(GLOBAL.document())
            .boundTo(this)
            .on("keydown", (ev) => this.playPauseKeyHandler(ev));

        this.eventBus.on(DIALOG_OPENED_EVENT, () => this.pause(), this);
        this.eventBus.on(DIALOG_CLOSED_EVENT, () => this.pause(), this);
    }

    protected playPauseKeyHandler(event: KeyboardEvent): void {
        if (event.key === KEYS.MEDIA_PLAY_PAUSE) {
            this.pausePlay(event);
        }
    }

    protected renderPausePlayButton(): Nullable<TemplateResult> {
        if (!this.videoInitialPlaying) {
            return null;
        }
        return html`
            <button class="pause-play-button"
                    type="button"
                    ?playing=${this.videoPlaying}
                    aria-label=${this.playPauseLabel()}
                    @click=${this.pausePlay}
            >${this.renderPausePlayIcon()}</button>
        `;
    }

    protected playPauseLabel(): string {
        if (this.videoPlaying) {
            return "Pause";
        } else {
            return "Play";
        }
    }

    protected renderPausePlayIcon(): TemplateResult {
        return html`
            <div class="pause">
                ${unsafeSVG(PAUSE_ICON)}
            </div>
            <div class="play">
                ${unsafeSVG(PLAY_ICON)}
            </div>
        `;
    }

    protected pausePlay(event: Event): void {
        this.clickTracking.handleButtonInteract(event, this.playPauseLabel());

        if (!this.videoElement) {
            return;
        }
        if (this.videoPlaying) {
            this.videoElement.pause();
            this.videoPlaying = false;
        } else {
            void this.videoElement.play();
            this.videoPlaying = true;
        }
    }

    public pause(): void {
        if (this.videoElement && this.videoPlaying) {
            this.videoElement.pause();
            this.videoPlaying = false;
        }
    }

    protected videoPlayingCallback(): void {
        this.videoInitialPlaying = true;
        this.videoPlaying = true;
    }
}

@customElement("eop-video-loop")
export class EopVideoLoop extends PausableVideoLoop {

    @property({attribute: "video-src-mobile"})
    private srcMobile: string;
    @state()
    private currentSrc: string;

    public constructor(
        private resolution: Resolution = resolve(Resolution)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.resolution.onBootstrapBreakpointChange(() => this.updateVideoSource());
        this.updateVideoSource();
    }

    public render(): TemplateResult {
        return html`
            <video @playing=${this.videoPlayingCallback} autoplay loop muted playsinline preload="metadata" aria-label="" class="video-loop">
                <source src=${this.currentSrc}>
                ${this.alt}
            </video>
            ${this.renderPausePlayButton()}
        `;
    }

    private updateVideoSource(): void {
        if (this.resolution.upTo(BOOTSTRAP.XS)) {
            this.currentSrc = this.srcMobile ?? this.src;
        } else {
            this.currentSrc = this.src;
        }
        this.videoElement?.load();
    }
}